import { Rect } from '../primitives/rect';
import { identityMatrix, transformPointByMatrix, rotateMatrix } from '../primitives/matrix';
import { getChildNode } from './dom-util';
import { Size } from '../primitives/size';
/**
 * Implements the basic functionalities
 */
/** @private */
export function randomId() {
    let chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz';
    let id = '';
    let num;
    for (let i = 0; i < 5; i++) {
        if ('crypto' in window && 'getRandomValues' in crypto) {
            let count = new Uint16Array(1);
            // tslint:disable-next-line:no-any
            let intCrypto = window.msCrypto || window.crypto;
            num = intCrypto.getRandomValues(count)[0] % (chars.length - 1);
        }
        else {
            num = Math.floor(Math.random() * chars.length);
        }
        if (i === 0 && num < 10) {
            i--;
            continue;
        }
        id += chars.substring(num, num + 1);
    }
    return id;
}
/** @private */
export function cornersPointsBeforeRotation(ele) {
    let bounds = new Rect();
    let top = ele.offsetY - ele.actualSize.height * ele.pivot.y;
    let bottom = ele.offsetY + ele.actualSize.height * (1 - ele.pivot.y);
    let left = ele.offsetX - ele.actualSize.width * ele.pivot.x;
    let right = ele.offsetX + ele.actualSize.width * (1 - ele.pivot.x);
    let topLeft = { x: left, y: top };
    let topCenter = { x: (left + right) / 2, y: top };
    let topRight = { x: right, y: top };
    let middleLeft = { x: left, y: (top + bottom) / 2 };
    let middleRight = { x: right, y: (top + bottom) / 2 };
    let bottomLeft = { x: left, y: bottom };
    let bottomCenter = { x: (left + right) / 2, y: bottom };
    let bottomRight = { x: right, y: bottom };
    bounds = Rect.toBounds([topLeft, topRight, bottomLeft, bottomRight]);
    return bounds;
}
/** @private */
export function rotateSize(size, angle) {
    let matrix = identityMatrix();
    rotateMatrix(matrix, angle, 0, 0);
    let topLeft = transformPointByMatrix(matrix, { x: 0, y: 0 });
    let topRight = transformPointByMatrix(matrix, { x: size.width, y: 0 });
    let bottomLeft = transformPointByMatrix(matrix, { x: 0, y: size.height });
    let bottomRight = transformPointByMatrix(matrix, { x: size.width, y: size.height });
    let minX = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    let minY = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    let maxX = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    let maxY = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    return new Size(maxX - minX, maxY - minY);
}
/** @private */
export function getBounds(element) {
    let bounds = new Rect();
    let corners;
    corners = cornersPointsBeforeRotation(element);
    let middleLeft = corners.middleLeft;
    let topCenter = corners.topCenter;
    let bottomCenter = corners.bottomCenter;
    let middleRight = corners.middleRight;
    let topLeft = corners.topLeft;
    let topRight = corners.topRight;
    let bottomLeft = corners.bottomLeft;
    let bottomRight = corners.bottomRight;
    element.corners = {
        topLeft: topLeft, topCenter: topCenter, topRight: topRight, middleLeft: middleLeft,
        middleRight: middleRight, bottomLeft: bottomLeft, bottomCenter: bottomCenter, bottomRight: bottomRight
    };
    if (element.rotateAngle !== 0 || element.parentTransform !== 0) {
        let matrix = identityMatrix();
        rotateMatrix(matrix, element.rotateAngle + element.parentTransform, element.offsetX, element.offsetY);
        element.corners.topLeft = topLeft = transformPointByMatrix(matrix, topLeft);
        element.corners.topCenter = topCenter = transformPointByMatrix(matrix, topCenter);
        element.corners.topRight = topRight = transformPointByMatrix(matrix, topRight);
        element.corners.middleLeft = middleLeft = transformPointByMatrix(matrix, middleLeft);
        element.corners.middleRight = middleRight = transformPointByMatrix(matrix, middleRight);
        element.corners.bottomLeft = bottomLeft = transformPointByMatrix(matrix, bottomLeft);
        element.corners.bottomCenter = bottomCenter = transformPointByMatrix(matrix, bottomCenter);
        element.corners.bottomRight = bottomRight = transformPointByMatrix(matrix, bottomRight);
        //Set corners based on rotate angle
    }
    bounds = Rect.toBounds([topLeft, topRight, bottomLeft, bottomRight]);
    element.corners.left = bounds.left;
    element.corners.right = bounds.right;
    element.corners.top = bounds.top;
    element.corners.bottom = bounds.bottom;
    element.corners.center = bounds.center;
    element.corners.width = bounds.width;
    element.corners.height = bounds.height;
    return bounds;
}
/** @private */
export function textAlignToString(value) {
    let state = '';
    switch (value) {
        case 'Center':
            state = 'center';
            break;
        case 'Left':
            state = 'left';
            break;
        case 'Right':
            state = 'right';
            break;
        case 'Justify':
            state = 'justify';
            break;
    }
    return state;
}
/** @private */
export function wordBreakToString(value) {
    let state = '';
    switch (value) {
        case 'Wrap':
            state = 'breakall';
            break;
        case 'NoWrap':
            state = 'keepall';
            break;
        case 'WrapWithOverflow':
            state = 'normal';
            break;
        case 'LineThrough':
            state = 'line-through';
            break;
    }
    return state;
}
export function bBoxText(textContent, options) {
    let measureElement = 'measureElement';
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'visible';
    // eslint-disable-next-line
    let svg = window[measureElement].children[2];
    let text = getChildNode(svg)[1];
    text.textContent = textContent;
    text.style.cssText = `font-size: ${options.fontSize}px; font-family: ${options.fontFamily};
    font-weight: ${options.bold ? 'bold' : 'normal'}`;
    let bBox = text.getBBox().width;
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'hidden';
    return bBox;
}
export function bBoxTextBlazor(textContent, options) {
    let measureElement = 'measureElement';
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'visible';
    // eslint-disable-next-line
    let svg = window[measureElement].children[2];
    let text = getChildNode(svg)[1];
    text.textContent = textContent;
    text.style.cssText = `font-size: ${options.fontSize}px; font-family: ${options.fontFamily};
    font-weight: ${options.bold ? 'bold' : 'normal'}`;
    const expandedString = expandTabsForSvg(text, textContent, { startX: 0, tabSizeSpaces: (7) });
    text.textContent = expandedString.expandedString;
    let bBox = expandedString.width;
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'hidden';
    return bBox;
}
export function expandTabsForSvg(textEl, input, { startX = 0, tabSizeSpaces = (7) } = {}) {
    // Measure the width of a single space in the current font.
    const original = textEl.textContent;
    textEl.textContent = ' ';
    const spaceW = textEl.getBBox().width || 0;
    textEl.textContent = original;
    const tabW = spaceW * tabSizeSpaces;
    let x = startX;
    let out = '';
    for (const ch of input) {
        if (ch === '\t') {
            // compute next tab stop in pixels
            const offset = x - startX;
            const nextOffset = Math.ceil(offset / tabW) * tabW;
            const padPx = nextOffset - offset;
            // convert pixel gap to a number of spaces (round up)
            const padSpaces = Math.max(1, Math.ceil(padPx / spaceW));
            out += ' '.repeat(padSpaces);
            x += padSpaces * spaceW;
        }
        else {
            // measure this glyph's width to advance cursor
            textEl.textContent = ch;
            const w = textEl.getBBox().width;
            out += ch;
            x += w;
        }
    }
    textEl.textContent = original;
    return { expandedString: out, width: x };
}
/** @private */
export function bBoxTextHeight(textContent, options) {
    let measureElement = 'measureElement';
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'visible';
    // eslint-disable-next-line
    let svg = window[measureElement].children[2];
    let text = getChildNode(svg)[1];
    text.textContent = textContent;
    text.style.cssText = `font-size: ${options.fontSize}px; font-family: ${options.fontFamily};
    font-weight: ${options.bold ? 'bold' : 'normal'}`;
    let bBox = text.getBBox().height;
    // eslint-disable-next-line
    window[measureElement].style.visibility = 'hidden';
    return bBox;
}
/** @private */
export function middleElement(i, j) {
    let m = 0;
    m = (i + j) / 2;
    return m;
}
/** @private */
export function whiteSpaceToString(value, wrap) {
    if (wrap === 'NoWrap' && value === 'PreserveAll') {
        return 'pre';
    }
    let state = '';
    switch (value) {
        case 'CollapseAll':
            state = 'nowrap';
            break;
        case 'CollapseSpace':
            state = 'pre-line';
            break;
        case 'PreserveAll':
            state = 'pre-wrap';
            break;
    }
    return state;
}
/** @private */
export function rotatePoint(angle, pivotX, pivotY, point) {
    if (angle !== 0) {
        let matrix = identityMatrix();
        rotateMatrix(matrix, angle, pivotX, pivotY);
        return transformPointByMatrix(matrix, point);
    }
    return point;
}
/** @private */
export function getOffset(topLeft, obj) {
    let offX = topLeft.x + obj.desiredSize.width * obj.pivot.x;
    let offY = topLeft.y + obj.desiredSize.height * obj.pivot.y;
    return {
        x: offX, y: offY
    };
}
